// src/components/ExploreComponent.js
import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  InputGroup,
  FormControl,
  Card,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { ActivitiesContext } from "../../context/ActivitiesContext";
import { fetchActivities as generateItinerary } from "../../_utils/api";
import PlaceDetailModal from "../../_components/main/PlaceDetailModal";

// 酒店配置信息
const HOTEL_CONFIG = {
  Basic: {
    name: "Travelodge Hotel by Wyndham Montreal Center",
    coords: [45.50832593783939, -73.5617548642837],
    priceRange: "$89-$129",
    rating: 4.0,
    amenities: ["Free WiFi", "Fitness Center", "24/7 Front Desk"],
    description:
      "Comfortable accommodations in the heart of Montreal with modern amenities and convenient location.",
    image:
      "https://www.wyndhamhotels.com/content/dam/property-images/en-us/tl/ca/qc/montreal/09782/09782_exterior_view_3.jpg",
  },
  Signature: {
    name: "Le Nouvel Hotel",
    coords: [45.4924495609113, -73.57605680684726],
    priceRange: "$159-$229",
    rating: 4.3,
    amenities: ["Spa", "Restaurant", "Room Service", "Concierge"],
    description:
      "Boutique hotel offering personalized service and elegant accommodations in Montreal's cultural district.",
    image:
      "https://www.tidan.com/wp-content/uploads/Tidan-Nouvel-Hotel-Main.jpg",
  },
  Luxury: {
    name: "Fairmont the Queen Elizabeth Hotel",
    coords: [45.50056265986229, -73.56804069317572],
    priceRange: "$279-$459",
    rating: 4.6,
    amenities: [
      "Fine Dining",
      "Spa",
      "Executive Lounge",
      "Valet Parking",
      "Business Center",
    ],
    description:
      "Iconic luxury hotel in downtown Montreal, offering world-class service and premium accommodations.",
    image:
      "https://phgcdn.com/images/uploads/YMQFT/masthead/1600x813-Fairmont-La-Reine-Elizabeth-Exterior-Night.jpg",
  },
};

const ExploreComponent = ({
  onLocationSelect,
  selectedLocations = [],
  onAddToFavorites,
  onAddToMyTrip,
  favorites = [],
  userBudgetLevel = "Signature",
}) => {
  const [activeTab, setActiveTab] = useState("For you");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState({
    locations: [],
    restaurants: [],
    activities: [],
    stays: [],
  });

  // 详情弹窗状态
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPlaceForModal, setSelectedPlaceForModal] = useState(null);

  const { planId, startDate, endDate } = useContext(ActivitiesContext);

  // 统一的菜单选项配置
  const menuTabs = [
    {
      key: "For you",
      label: "For you",
      icon: "bi-stars",
      color: "dark",
    },
    {
      key: "Stays",
      label: "Stays",
      icon: "bi-house-door",
      color: "dark",
    },
    {
      key: "Restaurants",
      label: "Restaurants",
      icon: "bi-cup-hot",
      color: "dark",
    },
    {
      key: "Things to do",
      label: "Things to do",
      icon: "bi-star",
      color: "dark",
    },
  ];

  // 生成基于HOTEL_CONFIG的酒店推荐
  const generateHotelRecommendations = () => {
    return Object.entries(HOTEL_CONFIG).map(([level, hotel], index) => ({
      id: `hotel-${level.toLowerCase()}`,
      name: hotel.name,
      location: "Montreal, Canada",
      image: hotel.image,
      rating: hotel.rating,
      reviews: Math.floor(Math.random() * 500) + 200,
      description: hotel.description,
      price: hotel.priceRange.split("-")[0],
      priceRange: hotel.priceRange,
      priceUnit: "per night",
      amenities: hotel.amenities,
      lat: hotel.coords[0],
      lng: hotel.coords[1],
      budgetLevel: level,
      place_id: `hotel_${level.toLowerCase()}`,
      fullData: {
        location: hotel.name,
        activity_desc: hotel.description,
        amenities: hotel.amenities,
        price_range: hotel.priceRange,
        rating: hotel.rating,
        latitude: hotel.coords[0],
        longitude: hotel.coords[1],
        image_url: hotel.image,
        budget_level: level,
        url: `https://booking.com/hotel/montreal/${level.toLowerCase()}`,
        avg_cost_per_person: parseInt(
          hotel.priceRange.split("-")[0].replace("$", "")
        ),
      },
    }));
  };

  // 从 API 获取推荐数据
 // 替换 fetchRecommendations 函数（约第 127-165 行）为以下代码：

const fetchRecommendations = async () => {
    setLoading(true);
    console.log('🚀 开始获取推荐数据...');
    
    try {
        const body = {
            plan_id: planId || 'explore_plan',
            start_date: startDate?.toISOString().slice(0, 10) || '2024-06-17',
            end_date: endDate?.toISOString().slice(0, 10) || '2024-06-21',
            arrival_time: '09:00 AM',
            explore_mode: true,
            preferences: {
                activity: 'Sightseeing',
                cuisine: 'Local Specialties',
                budget: userBudgetLevel
            }
        };

        console.log('📤 请求体:', body);

        const res = await generateItinerary(body);
        
        console.log('📥 API响应:', res);
        console.log('📥 响应类型:', typeof res);
        console.log('📥 是否有 multi_day_itinerary:', !!res?.multi_day_itinerary);

        if (res && res.multi_day_itinerary) {
            console.log('✅ 找到 multi_day_itinerary，开始处理...');
            console.log('📊 multi_day_itinerary 数据结构:', res.multi_day_itinerary);
            
            // 检查数据结构
            if (Array.isArray(res.multi_day_itinerary)) {
                console.log(`📋 multi_day_itinerary 是数组，长度: ${res.multi_day_itinerary.length}`);
                
                // 检查每一天的数据
                res.multi_day_itinerary.forEach((day, index) => {
                    console.log(`📅 Day ${index}:`, {
                        hasData: !!day,
                        hasPlan: !!day?.plan,
                        planLength: day?.plan?.length || 0,
                        dayStructure: day
                    });
                    
                    // 检查每个计划项的 activity 字段
                    if (day?.plan && Array.isArray(day.plan)) {
                        day.plan.forEach((item, itemIndex) => {
                            if (item.activity !== undefined && item.activity !== null) {
                                console.log(`  📍 Item ${itemIndex} activity:`, {
                                    value: item.activity,
                                    type: typeof item.activity,
                                    isString: typeof item.activity === 'string'
                                });
                            }
                        });
                    }
                });
            } else {
                console.warn('⚠️ multi_day_itinerary 不是数组:', typeof res.multi_day_itinerary);
            }

            // 安全的分类函数
            const safeCategorizeApiData = (itineraryData) => {
                console.log('🔍 开始安全分类...');
                
                // 确保 itineraryData 是数组
                if (!Array.isArray(itineraryData)) {
                    console.error('❌ itineraryData 不是数组，返回空结果');
                    return { locations: [], restaurants: [], activities: [], stays: [] };
                }

                try {
                    // 调用原始的 categorizeApiData，但包装在 try-catch 中
                    return categorizeApiData(itineraryData);
                } catch (error) {
                    console.error('❌ 分类过程中出错:', error);
                    console.error('❌ 错误堆栈:', error.stack);
                    return { locations: [], restaurants: [], activities: [], stays: [] };
                }
            };

            const categorizedData = safeCategorizeApiData(res.multi_day_itinerary);
            console.log('✅ 分类完成:', {
                locations: categorizedData.locations.length,
                restaurants: categorizedData.restaurants.length,
                activities: categorizedData.activities.length,
                stays: categorizedData.stays.length
            });

            // 添加酒店推荐
            categorizedData.stays = [
                ...generateHotelRecommendations(),
                ...categorizedData.stays
            ];
            
            console.log('✅ 添加酒店后的数据:', {
                totalStays: categorizedData.stays.length
            });

            setRecommendations(categorizedData);
        } else {
            console.warn('⚠️ 没有找到 multi_day_itinerary，使用静态数据');
            const staticData = getStaticRecommendations();
            staticData.stays = generateHotelRecommendations();
            setRecommendations(staticData);
        }
    } catch (error) {
        console.error('❌ 获取推荐失败 - 详细错误信息:');
        console.error('错误对象:', error);
        console.error('错误消息:', error.message);
        console.error('错误堆栈:', error.stack);
        
        // 使用静态数据作为后备
        console.log('💡 使用静态数据作为后备方案');
        const staticData = getStaticRecommendations();
        staticData.stays = generateHotelRecommendations();
        setRecommendations(staticData);
    } finally {
        setLoading(false);
        console.log('🏁 获取推荐流程结束');
    }
};

  const categorizeApiData = (itineraryData) => {
    console.log('🔍 开始分类API数据:', itineraryData);

    // 安全的字符串转换函数
    const safeString = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return '[object]';
            }
        }
        return String(value);
    };

    // 根据 activity 字段进行精确分类
    const categorizeByActivity = (item) => {
        // 使用安全的字符串转换
        const activity = safeString(item.activity).toLowerCase().trim();
        const cuisine = safeString(item.cuisine).trim();
        const location = safeString(item.location).toLowerCase().trim();

        console.log(`🔍 分类分析:`, {
            name: item.location || item.name || 'unknown',
            activity: activity,
            activityOriginal: item.activity,
            activityType: typeof item.activity,
            cuisine: `"${cuisine}"`,
            cuisineLength: cuisine.length,
            cuisineEmpty: cuisine === ''
        });

        // 1. 餐厅分类 - 只有当 cuisine 字段有实际内容时才算餐厅
        if (cuisine && cuisine !== '' && cuisine !== 'null' && cuisine !== 'undefined' && cuisine.length > 0) {
            console.log(`✅ 因为有cuisine内容而归类为餐厅`);
            return 'restaurant';
        }

        // 2. 根据 activity 字段分类
        switch (activity) {
            // 餐厅相关
            case 'dining':
            case 'food':
            case 'restaurant':
            case 'lunch':
            case 'dinner':
            case 'breakfast':
            case 'brunch':
                console.log(`✅ 因为activity是餐厅相关而归类为餐厅`);
                return 'restaurant';

            // 住宿相关
            case 'accommodation':
            case 'hotel':
            case 'stay':
            case 'check-in':
            case 'check-out':
                console.log(`✅ 因为activity是住宿相关而归类为住宿`);
                return 'stay';

            // 观光活动
            case 'sightseeing':
            case 'tour':
            case 'visit':
            case 'museum':
            case 'gallery':
            case 'attraction':
            case 'entertainment':
            case 'shopping':
            case 'experience':
            case 'activity':
                console.log(`✅ 因为activity是观光相关而归类为活动`);
                return 'activity';

            // 默认情况：根据其他线索判断
            default:
                // 检查是否包含餐厅关键词
                if (location.includes('restaurant') || 
                    location.includes('cafe') || 
                    location.includes('bistro') ||
                    location.includes('bakery') ||
                    location.includes('diner')) {
                    console.log(`✅ 因为location包含餐厅关键词而归类为餐厅`);
                    return 'restaurant';
                }
                // 检查是否包含酒店关键词  
                if (location.includes('hotel') || 
                    location.includes('inn') || 
                    location.includes('resort') || 
                    location.includes('château') ||
                    location.includes('motel')) {
                    console.log(`✅ 因为location包含住宿关键词而归类为住宿`);
                    return 'stay';
                }
                // 其他归类为普通位置
                console.log(`✅ 默认归类为位置`);
                return 'location';
        }
    };

    const locations = [];
    const restaurants = [];
    const activities = [];
    const stays = [];

    // 安全遍历数据
    if (!Array.isArray(itineraryData)) {
        console.error('❌ itineraryData 不是数组');
        return { locations, restaurants, activities, stays };
    }

    itineraryData.forEach((day, dayIndex) => {
        if (!day || !day.plan || !Array.isArray(day.plan)) {
            console.warn(`⚠️ Day ${dayIndex} 没有有效的 plan 数据`);
            return;
        }

        day.plan.forEach((item, itemIndex) => {
            try {
                console.log(`🎯 处理项目 ${dayIndex}-${itemIndex}:`, {
                    location: item.location,
                    activity: item.activity,
                    activityType: typeof item.activity,
                    cuisine: item.cuisine,
                    activity_desc: item.activity_desc?.substring(0, 50) + '...'
                });

                const recommendation = {
                    id: `${dayIndex}-${itemIndex}`,
                    name: item.location || item.activity || item.itinerary_name || 'Unknown',
                    location: item.location || 'Montreal, Canada',
                    image: item.image_url || getDefaultImage(item),
                    rating: item.rating || generateRating(),
                    reviews: item.reviews || Math.floor(Math.random() * 1000) + 100,
                    description: item.activity_desc || item.description || '',
                    time: item.start_time && item.end_time
                        ? `${item.start_time} - ${item.end_time}`
                        : item.start_time || '',
                    cost: item.avg_cost_per_person || 0,
                    cuisine: item.cuisine || '',
                    price_level: item.price_level || '',
                    website: item.url || '',
                    place_id: item.place_id || `place_${dayIndex}_${itemIndex}`,
                    lat: item.latitude || (45.5017 + (Math.random() - 0.5) * 0.1),
                    lng: item.longitude || (-73.5673 + (Math.random() - 0.5) * 0.1),
                    fullData: item
                };

                // 跳过酒店入住项目
                const locationLower = safeString(item.location).toLowerCase();
                const activityLower = safeString(item.activity).toLowerCase();
                
                if (item.place_id === 'hotel_checkin' ||
                    locationLower.includes('hotel check') ||
                    activityLower.includes('hotel check')) {
                    console.log('⭐️ 跳过酒店入住项目');
                    return;
                }

                // 根据 activity 字段进行精确分类
                const categoryType = categorizeByActivity(item);

                switch (categoryType) {
                    case 'restaurant':
                        console.log('🍽️ 归类为餐厅:', recommendation.name);
                        restaurants.push(recommendation);
                        break;
                    case 'stay':
                        console.log('🏨 归类为住宿:', recommendation.name);
                        stays.push(recommendation);
                        break;
                    case 'activity':
                        console.log('🎪 归类为活动:', recommendation.name);
                        activities.push(recommendation);
                        break;
                    default:
                        console.log('📍 归类为位置:', recommendation.name);
                        locations.push(recommendation);
                        break;
                }
            } catch (itemError) {
                console.error(`❌ 处理项目 ${dayIndex}-${itemIndex} 时出错:`, itemError);
                console.error('问题项目:', item);
            }
        });
    });

    console.log('📊 分类结果:', {
        restaurants: restaurants.length,
        activities: activities.length,
        locations: locations.length,
        stays: stays.length
    });

    return { locations, restaurants, activities, stays };
};

  const generateRating = () => (Math.random() * 1.5 + 3.5).toFixed(1);

  const getDefaultImage = (item) => {
    const defaultImages = {
      restaurant:
        "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800",
      hotel: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800",
      activity:
        "https://images.unsplash.com/photo-1539650116574-75c0c6d73c3e?w=800",
      location:
        "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2a/ce/de/b4/caption.jpg?w=800",
    };

    if (item.cuisine) return defaultImages.restaurant;
    if (item.place_id === "hotel_checkin") return defaultImages.hotel;
    if (item.activity_desc) return defaultImages.activity;
    return defaultImages.location;
  };

  const getStaticRecommendations = () => ({
    locations: [
      {
        id: "static-1",
        name: "Old Montreal",
        location: "Montreal, Canada",
        image:
          "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2a/ce/de/b4/caption.jpg?w=800",
        rating: 4.5,
        reviews: 1234,
        lat: 45.5088,
        lng: -73.5541,
        description: "Historic cobblestone streets and charming architecture",
        place_id: "old_montreal_001",
        fullData: {
          location: "Old Montreal",
          activity_desc:
            "Historic cobblestone streets and charming architecture",
          start_time: "09:00",
          end_time: "12:00",
          avg_cost_per_person: 0,
          image_url:
            "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2a/ce/de/b4/caption.jpg?w=800",
        },
      },
      {
        id: "static-2",
        name: "Mount Royal Park",
        location: "Montreal, Canada",
        image:
          "https://images.unsplash.com/photo-1539650116574-75c0c6d73c3e?w=800",
        rating: 4.3,
        reviews: 892,
        lat: 45.5088,
        lng: -73.5878,
        description: "Beautiful park with panoramic city views",
        place_id: "mount_royal_park_001",
        fullData: {
          location: "Mount Royal Park",
          activity_desc: "Beautiful park with panoramic city views",
          start_time: "14:00",
          end_time: "17:00",
          avg_cost_per_person: 0,
          image_url:
            "https://images.unsplash.com/photo-1539650116574-75c0c6d73c3e?w=800",
        },
      },
    ],
    restaurants: [
      {
        id: "static-r1",
        name: "Toqué!",
        location: "Downtown Montreal",
        image:
          "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800",
        rating: 4.6,
        reviews: 892,
        cuisine: "French",
        lat: 45.5017,
        lng: -73.5673,
        cost: 85,
        description: "Fine dining with innovative Quebec cuisine",
        place_id: "toque_restaurant_001",
        fullData: {
          location: "Toqué!",
          activity_desc: "Fine dining with innovative Quebec cuisine",
          cuisine: "French",
          start_time: "19:00",
          end_time: "22:00",
          avg_cost_per_person: 85,
          image_url:
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800",
        },
      },
    ],
    activities: [],
    stays: [],
  });

  useEffect(() => {
    fetchRecommendations();
  }, [planId, startDate, endDate, userBudgetLevel]);

  // 处理地点点击 - 显示详情弹窗
  const handleLocationClick = (location) => {
    console.log("🎯 Location clicked for details:", location);

    setSelectedPlaceForModal({
      data: location.fullData || location,
      name: location.name,
    });
    setShowDetailModal(true);

    if (onLocationSelect) {
      onLocationSelect(location);
    }
  };

  // 处理收藏点击
  const handleFavoriteClick = (e, location) => {
    e.stopPropagation();
    console.log("❤️ Favorite clicked:", location);

    if (onAddToFavorites) {
      onAddToFavorites(location);
    }
  };

  // 处理添加到我的行程
  const handleAddToMyTrip = (location) => {
    console.log("➕ Add to my trip:", location);

    if (onAddToMyTrip) {
      onAddToMyTrip(location);
    }

    setShowDetailModal(false);
    
  };

  // 关闭详情弹窗
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedPlaceForModal(null);
  };

  const isLocationSelected = (locationId) => {
    return selectedLocations.some((loc) => loc.id === locationId);
  };

  const isFavorited = (locationId) => {
    return favorites.includes(locationId);
  };

  const renderStarRating = (rating) => {
    const numRating = parseFloat(rating);
    const stars = [];
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} style={{ color: "#ffc107" }}>
          ★
        </span>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <span key="half" style={{ color: "#ffc107" }}>
          ☆
        </span>
      );
    }

    const emptyStars = 5 - Math.ceil(numRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <span key={`empty-${i}`} style={{ color: "#dee2e6" }}>
          ☆
        </span>
      );
    }

    return stars;
  };

  const renderRecommendationCard = (item, type = "location") => (
    <Card
      key={item.id}
      className={`border-0 shadow-sm ${
        isLocationSelected(item.id) ? "border-primary border-2" : ""
      }`}
      style={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        height: "320px",
        width: "100%",
      }}
      onClick={() => handleLocationClick(item)}
    >
      <div
        style={{ position: "relative", height: "150px", overflow: "hidden" }}
      >
        <Card.Img
          variant="top"
          src={item.image}
          style={{
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.3s ease",
          }}
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1539650116574-75c0c6d73c3e?w=800";
          }}
        />

        {isLocationSelected(item.id) && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0, 123, 255, 0.3)" }}
          >
            <div
              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              ✓
            </div>
          </div>
        )}

        <Button
          variant="link"
          className="position-absolute top-0 end-0 m-2"
          style={{
            fontSize: "1.2rem",
            color: isFavorited(item.id) ? "#e74c3c" : "white",
            textShadow: "0 0 3px rgba(0,0,0,0.5)",
          }}
          onClick={(e) => handleFavoriteClick(e, item)}
        >
          <i
            className={`bi ${
              isFavorited(item.id) ? "bi-heart-fill" : "bi-heart"
            }`}
          ></i>
        </Button>

        {type === "stay" && item.budgetLevel && (
          <div
            className={`position-absolute top-0 start-0 m-2 px-2 py-1 rounded small text-white ${
              item.budgetLevel === "Basic"
                ? "bg-success"
                : item.budgetLevel === "Signature"
                ? "bg-warning"
                : "bg-danger"
            }`}
          >
            {item.budgetLevel}
          </div>
        )}

        {item.cuisine && (
          <div className="position-absolute bottom-0 start-0 m-2 bg-dark text-white px-2 py-1 rounded small">
            {item.cuisine}
          </div>
        )}
      </div>

      <Card.Body className="p-3 d-flex flex-column" style={{ height: "170px" }}>
        <div
          className="d-flex justify-content-between align-items-start mb-2"
          style={{ minHeight: "45px" }}
        >
          <h6
            className="mb-0 fw-bold flex-grow-1"
            style={{
              fontSize: "0.95rem",
              lineHeight: "1.3",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item.name}
          </h6>
          {type === "stay" ? (
            <div className="text-end ms-2" style={{ minWidth: "70px" }}>
              <div className="fw-bold" style={{ fontSize: "0.85rem" }}>
                {item.priceRange || item.price}
              </div>
              <small className="text-muted">{item.priceUnit}</small>
            </div>
          ) : (
            <div
              className="d-flex align-items-center ms-2"
              style={{ fontSize: "0.8rem" }}
            >
              {renderStarRating(item.rating)}
              <small className="text-muted ms-1">({item.reviews})</small>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center text-muted">
            <i className="bi bi-geo-alt me-1"></i>
            <small style={{ fontSize: "0.8rem" }}>{item.location}</small>
          </div>

          {item.cost && (
            <small className="text-success fw-bold">${item.cost}</small>
          )}
        </div>

        {item.description && (
          <div className="mb-2 flex-grow-1">
            <small
              className="text-muted"
              style={{
                fontSize: "0.8rem",
                lineHeight: "1.3",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {item.description}
            </small>
          </div>
        )}

        {type === "stay" && item.amenities && (
          <div className="mt-auto">
            <div className="d-flex flex-wrap gap-1">
              {item.amenities.slice(0, 2).map((amenity, index) => (
                <span
                  key={index}
                  className="badge bg-light text-dark"
                  style={{ fontSize: "0.7rem" }}
                >
                  {amenity}
                </span>
              ))}
              {item.amenities.length > 2 && (
                <span
                  className="badge bg-light text-dark"
                  style={{ fontSize: "0.7rem" }}
                >
                  +{item.amenities.length - 2}
                </span>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const getCurrentTabData = () => {
    switch (activeTab) {
      case "Stays":
        return recommendations.stays.sort((a, b) => {
          if (a.budgetLevel === userBudgetLevel) return -1;
          if (b.budgetLevel === userBudgetLevel) return 1;
          return 0;
        });
      case "Restaurants":
        return recommendations.restaurants;
      case "Things to do":
        return recommendations.activities;
      default:
        return [...recommendations.locations, ...recommendations.restaurants];
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <p className="text-muted">Loading Montreal recommendations...</p>
      </div>
    );
  }

  return (
    <div className="h-100 d-flex flex-column">
      {/* Header */}
      <div className="border-bottom bg-white">
        <div className="d-flex align-items-center justify-content-between p-3">
          <h4 className="mb-0">🔍 Explore Montreal</h4>
          <div className="d-flex align-items-center gap-2">
            <span
              className={`badge ${
                userBudgetLevel === "Basic"
                  ? "bg-success"
                  : userBudgetLevel === "Signature"
                  ? "bg-warning"
                  : "bg-danger"
              }`}
            >
              {userBudgetLevel} Budget
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={fetchRecommendations}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-3 pb-3">
          <InputGroup>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <FormControl
              placeholder="Search recommendations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        {/* 统一风格的Navigation Tabs */}
        <div
          className="d-flex border-bottom px-3"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          {menuTabs.map((tab) => (
            <Button
              key={tab.key}
              variant="link"
              className={`text-decoration-none px-3 py-2 border-0 rounded-0 d-flex align-items-center gap-2 ${
                activeTab === tab.key
                  ? "text-dark border-bottom border-dark border-3 bg-white"
                  : "text-muted bg-transparent"
              }`}
              style={{
                fontWeight: activeTab === tab.key ? "600" : "400",
                transition: "all 0.2s ease",
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`${tab.icon}`} style={{ fontSize: "0.9rem" }}></i>
              <span style={{ fontSize: "0.9rem" }}>{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* 状态提示 */}
        <div className="px-3 py-2 bg-light d-flex justify-content-between align-items-center">
          <div>
            {selectedLocations.length > 0 && (
              <small className="text-primary me-3">
                <i className="bi bi-check-circle me-1"></i>
                {selectedLocations.length} selected for map
              </small>
            )}
            {favorites.length > 0 && (
              <small className="text-danger">
                <i className="bi bi-heart-fill me-1"></i>
                {favorites.length} favorites
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-grow-1 overflow-auto p-3">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
            alignItems: "start",
          }}
        >
          {getCurrentTabData()
            .filter(
              (item) =>
                searchTerm === "" ||
                item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.location.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((item) =>
              renderRecommendationCard(
                item,
                activeTab === "Stays" ? "stay" : "location"
              )
            )}
        </div>

        {getCurrentTabData().length === 0 && (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              No recommendations found for "{activeTab}"
            </p>
            <Button variant="primary" onClick={fetchRecommendations}>
              Try Again
            </Button>
          </div>
        )}
      </div>
      {/* 详情弹窗 */}
      <PlaceDetailModal
        show={showDetailModal}
        onHide={handleCloseModal}
        placeData={selectedPlaceForModal?.data}
        placeName={selectedPlaceForModal?.name}
        additionalActions={
          <Button
            variant="success"
            className="me-2"
            onClick={() => handleAddToMyTrip(selectedPlaceForModal?.data)}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add to My Trip
          </Button>
        }
      />
    </div>
  );
};

export default ExploreComponent;
